import type { ProposalNodeSet, TimberListItem } from '../types';
import { lineKey } from './proposalTimberDiff';
import { isDefaultSpeciesGrade, isExplicitTimberGrade } from './timberMaterialParser';

const DEFAULT_DOUGLAS_FIR_GRADE = '#1';

function preferProposalLine(existing: TimberListItem | undefined, candidate: TimberListItem): TimberListItem {
  if (!existing) return candidate;
  const existingGrade = existing.grade?.trim() || '';
  const candidateGrade = candidate.grade?.trim() || '';
  if (!existingGrade) return candidate;
  if (!candidateGrade) return existing;
  if (isDefaultSpeciesGrade(existingGrade, existing.species) && isExplicitTimberGrade(candidateGrade, candidate.species)) {
    return candidate;
  }
  return existing;
}

function indexProposalLines(proposalNodeSets: ProposalNodeSet[]): {
  byLineNumber: Map<number, TimberListItem>;
  byProductionNumber: Map<string, TimberListItem>;
  byMatchKey: Map<string, TimberListItem>;
} {
  const byLineNumber = new Map<number, TimberListItem>();
  const byProductionNumber = new Map<string, TimberListItem>();
  const byMatchKey = new Map<string, TimberListItem>();

  for (const ns of proposalNodeSets) {
    for (const line of ns.materials?.timberList ?? []) {
      if (line.sourceLineNumber != null) {
        const prev = byLineNumber.get(line.sourceLineNumber);
        byLineNumber.set(line.sourceLineNumber, preferProposalLine(prev, line));
      }
      if (line.sourceProductionNumber?.trim()) {
        const prod = line.sourceProductionNumber.trim();
        const prev = byProductionNumber.get(prod);
        byProductionNumber.set(prod, preferProposalLine(prev, line));
      }
      const key = lineKey(line.name, line.dimensions);
      const prev = byMatchKey.get(key);
      byMatchKey.set(key, preferProposalLine(prev, line));
    }
  }

  return { byLineNumber, byProductionNumber, byMatchKey };
}

function linesAgree(a: TimberListItem, b: TimberListItem): boolean {
  return lineKey(a.name, a.dimensions) === lineKey(b.name, b.dimensions);
}

function matchProposalLine(
  item: TimberListItem,
  indexes: ReturnType<typeof indexProposalLines>,
): TimberListItem | undefined {
  const key = lineKey(item.name, item.dimensions);
  const byKey = indexes.byMatchKey.get(key);
  if (byKey?.grade?.trim()) return byKey;

  if (item.sourceLineNumber != null) {
    const byLine = indexes.byLineNumber.get(item.sourceLineNumber);
    if (byLine?.grade?.trim() && linesAgree(item, byLine)) return byLine;
  }

  if (item.sourceProductionNumber?.trim()) {
    const byProd = indexes.byProductionNumber.get(item.sourceProductionNumber.trim());
    if (byProd?.grade?.trim() && linesAgree(item, byProd)) return byProd;
  }

  return byKey;
}

function shouldApplyProposalGrade(item: TimberListItem, match: TimberListItem): boolean {
  const proposalGrade = match.grade?.trim() || '';
  if (!proposalGrade) return false;

  const csvGrade = item.grade?.trim() || '';
  if (isExplicitTimberGrade(csvGrade, item.species)) {
    return false;
  }

  return isDefaultSpeciesGrade(csvGrade, item.species)
    && isExplicitTimberGrade(proposalGrade, match.species);
}

/**
 * When a staged hub CSV is published, apply proposal species/grade only when the
 * CSV line still has a species default grade (e.g. #1) and the proposal has an
 * explicit grade (e.g. Select). Never overwrite CSV-parsed Select with proposal #1.
 */
export function overlayProposalTimberGradesOnNodeSets(
  nodeSets: ProposalNodeSet[],
  proposalNodeSets: ProposalNodeSet[],
): ProposalNodeSet[] {
  if (!proposalNodeSets.length || !nodeSets.length) return nodeSets;

  const indexes = indexProposalLines(proposalNodeSets);

  return nodeSets.map(ns => ({
    ...ns,
    materials: {
      ...ns.materials,
      timberList: (ns.materials.timberList ?? []).map(item => {
        const match = matchProposalLine(item, indexes);
        if (!match || !shouldApplyProposalGrade(item, match)) return item;
        return {
          ...item,
          grade: match.grade,
          species: match.species?.trim() ? match.species : item.species,
        };
      }),
    },
  }));
}
